<template>
    <div class="password-strength">
        <transition name="fade">
            <div v-show="visible" class="information">
                <div class="arrow"></div>
                <h5 class="heading-normal" :class="`${strength}`">
                    {{ $t('password-strength.title') }}:
                    <span v-if="$v.password.required">{{ $t(`password-strength.strengths.${strength}`) }}</span>
                </h5>
                <div class="meter" :class="`${strength}`">
                    <div v-for="i in 4" :key="i"></div>
                </div>
                <div class="rules">
                    <div
                        v-for="rule in rules"
                        :key="rule"
                        class="rule"
                        :class="{ valid: $v.password.required && $v.password[getValidatorId(rule)] }"
                    >
                        <svgCheckmarkThick
                            v-if="$v.password.required && $v.password[getValidatorId(rule)]"
                        ></svgCheckmarkThick>
                        <svgCloseLarge v-else class="invalid"></svgCloseLarge>
                        {{ $t(`password-strength.rules.${rule}`, [minPasswordLength, maxPasswordLength]) }}
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import svgCheckmarkThick from '@/images/svg/checkmark-thick.svg';
import svgCloseLarge from '@/images/svg/close-large.svg';
import { required, minLength, not, sameAs } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
const DEFAULT_MIN_PASSWORD_LENGTH = 8;
const DEFAULT_MAX_PASSWORD_LENGTH = 32;

export default {
    name: 'PasswordStrength',
    components: {
        svgCheckmarkThick,
        svgCloseLarge,
    },
    props: {
        password: {
            type: String,
            default: '',
        },
        visible: {
            type: Boolean,
            default: false,
        },
        email: {
            type: String,
            default: '',
        },
        minPasswordLength: {
            type: Number,
            default: DEFAULT_MIN_PASSWORD_LENGTH,
        },
        maxPasswordLength: {
            type: Number,
            default: DEFAULT_MAX_PASSWORD_LENGTH,
        },
        isSignup: {
            type: Boolean,
            default: false,
        },
        submitted: {
            type: Boolean,
            default: false,
        },
        isSelfService: {
            type: Boolean,
            default: false,
        },
        isToy: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            rules: ['length', 'uppercase', 'lowercase', 'number', 'symbol'],
        };
    },
    computed: {
        ...mapGetters({
            serialNumber: 'selfService/getSerialNumber',
        }),
        strength() {
            if (!this.$v.password.required) {
                this.setErrorMessage(this.submitted ? this.$t('error.input.newPassword') : '');
                return '';
            }
            if (!this.$v.password.noSpaces) {
                this.setErrorMessage(this.$t('error.form.20197')); // PASSWORD_INVALID_WHITE_SPACE
                return 'weak';
            }
            if (!this.$v.password.notSameAsEmail) {
                this.setErrorMessage(this.$t('error.form.20198')); // PASSWORD_INVALID_SAME_AS_EMAIL
                return 'weak';
            }
            if (!this.$v.password.minLength) {
                if (this.matchCount <= 1) {
                    this.setErrorMessage(this.$t('error.form.20195')); // PASSWORD_INVALID_LENGTH
                    return 'very-weak';
                }
                this.setErrorMessage(this.$t('error.form.20194')); // PASSWORD_INVALID_WEAK
                return 'weak';
            }
            if (this.matchCount <= 2) {
                this.setErrorMessage(this.$t('error.form.20194')); // PASSWORD_INVALID_WEAK
                return 'weak';
            }
            this.setErrorMessage('');
            if (this.matchCount === 3) {
                return 'medium';
            }
            return 'strong';
        },
        matchCount() {
            let count = 0;
            if (this.$v.password.uppercase) count++;
            if (this.$v.password.lowercase) count++;
            if (this.$v.password.number) count++;
            if (this.$v.password.symbol) count++;
            return count;
        },
        isUnified() {
            return this.$router.currentRoute.query.unified || this.$router.currentRoute.name.indexOf('unified') > -1;
        },
    },
    watch: {
        visible(newVal, oldVal) {
            if (
                this.password &&
                newVal !== oldVal &&
                newVal === false &&
                ['medium', 'strong'].includes(this.strength)
            ) {
                this.checkValidPass();
            }
        },
    },
    validations() {
        return {
            password: {
                required,
                minLength: minLength(this.minPasswordLength),
                uppercase: (value) => /[A-Z]/.test(value),
                lowercase: (value) => /[a-z]/.test(value),
                number: (value) => /[0-9]/.test(value),
                // eslint-disable-next-line no-useless-escape
                symbol: (value) => /[$-/@#:-?{-~!"^_`\[\]]/.test(value),
                noSpaces: (value) => value.indexOf(' ') === -1,
                notSameAsEmail: not(sameAs('email')),
            },
        };
    },
    mounted() {},
    methods: {
        setErrorMessage(message) {
            this.$emit('setErrorMessage', message);
        },
        getValidatorId(rule) {
            return rule === 'length' ? 'minLength' : rule;
        },
        async checkValidPass() {
            try {
                if (this.isToy) {
                    await this.$accountAPI.post('no-auth/password/strength', {
                        password: this.$v.password.$model,
                        ...(this.email ? { email: this.email } : {}),
                    });
                } else if (this.isSelfService) {
                    await this.$selfServiceAPI.post('/no-auth/op/password/strength', {
                        newPassword: this.$v.password.$model,
                        serialNumber: this.serialNumber,
                    });
                } else {
                    await this.$regionalAPI.post(
                        `no-auth/${this.isUnified ? 'unified/' : ''}${this.isSignup ? 'signup/' : ''}password/strength`,
                        {
                            password: this.$v.password.$model,
                            ...(this.email ? { email: this.email } : {}),
                        }
                    );
                }
            } catch ({ errorCode }) {
                if (!errorCode) return;
                this.setErrorMessage(this.$t(`error.form.${errorCode}`));
            }
        },
    },
};
</script>

<style lang="less" scoped>
@very-weak-color: #f44336;
@weak-color: #ff9800;
@medium-color: #03a9f4;
@strong-color: #4caf50;

.password-strength {
    cursor: default;
    margin: 10px 0;
    .heading-normal {
        margin: 0 0 10px 0;
        &.very-weak {
            color: @very-weak-color;
        }
        &.weak {
            color: @weak-color;
        }
        &.medium {
            color: @medium-color;
        }
        &.strong {
            color: @strong-color;
        }
        span {
            font-weight: bold;
            margin-left: 5px;
            text-transform: uppercase;
        }
    }
    .meter {
        display: flex;
        height: 5px;
        margin-bottom: 15px;
        width: 100%;
        &.very-weak {
            :first-child {
                background-color: @very-weak-color;
            }
        }
        &.weak {
            :first-child,
            :nth-child(2) {
                background-color: @weak-color;
            }
        }
        &.medium {
            :first-child,
            :nth-child(2),
            :nth-child(3) {
                background-color: @medium-color;
            }
        }
        &.strong {
            div {
                background-color: @strong-color;
            }
        }
        div {
            background-color: #868686;
            height: 100%;
            margin-right: 5px;
            width: 100%;
            &:last-child {
                margin: 0;
            }
        }
    }
    .rules {
        color: #808080;
        .rule {
            display: flex;
            margin: 4px 0;
            &.valid {
                color: white;
            }
            &:last-child {
                margin: 0;
            }
            svg {
                margin-right: 3px;
                width: 15px;
                &.invalid {
                    opacity: 0.5;
                }
            }
        }
    }

    .information {
        position: relative;
        background-color: #171a21;
        margin-top: 15px;
        padding: 20px 20px 15px 20px;
        border: 1px solid #333740;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.5), 0 6px 20px 0 rgba(0, 0, 0, 0.5);
        h5 {
            font-size: 15px;
            color: #fff;
        }
    }
    .arrow {
        position: absolute;
        background-color: #171a21;
        top: -9px;
        left: 48%;
        width: 16px;
        height: 16px;
        border-left: 1px solid #333740;
        border-top: 1px solid #333740;
        transform: rotate(45deg);
    }
}
</style>
